import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchGalleries } from "../lib/cms";

export default function Treatments() {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadGallery() {
      try {
        const galleries = await fetchGalleries();
        const behandlingerGallery = galleries.find(
          (g) => g.slug?.da === "behandlinger" || g.slug === "behandlinger"
        );
        if (behandlingerGallery && behandlingerGallery.images) {
          setGalleryImages(behandlingerGallery.images);
        }
      } catch (error) {
        console.error("Error loading treatments gallery:", error);
      } finally {
        setLoading(false);
      }
    }
    loadGallery();
  }, []);

  const treatments = [
    {
      title: "Ryg",
      slug: "ryg",
      icon: "fa-light fa-spine",
      desc: "Rygsmerter er en af de hyppigste folkesygdomme. Vi tilbyder grundig undersøgelse og individuel behandling af alt fra hold i ryggen og lændesmerter til diskusprolaps og spinalstenose.",
      focus: "Fokus på tryghed, grundig undersøgelse og individuel vurdering.",
      href: "/behandlinger/ryg",
    },
    {
      title: "Arme og ben",
      slug: "arme-og-ben",
      icon: "fa-light fa-joint",
      desc: "Smerter i arme og ben kan stamme fra muskler, led eller sener. Vi behandler sportsskader, løberknæ, springerknæ, tennisalbue, golfalbue, forstuvninger og hælspore.",
      focus: "Fokus på bevægelighed, smerter, belastning og funktion.",
      href: "/behandlinger/arme-og-ben",
    },
    {
      title: "Skulder",
      slug: "skulder",
      icon: "fa-light fa-child-reaching",
      desc: "Skulderen er et komplekst led med stor bevægelighed. Vi hjælper dig med skuldersmerter, indeklemningssyndrom (impingement), senebetændelse og frossen skulder.",
      focus: "Fokus på bevægelighed, smerter og skånsom behandling.",
      href: "/behandlinger/skulder",
    },
    {
      title: "Hofte",
      slug: "hofte",
      icon: "fa-light fa-person-walking",
      desc: "Hoftesmerter kan begrænse din gang og mobilitet. Vi undersøger og behandler hoftesmerter, slimsæksbetændelse, slidgigt og bækkenproblemer med fokus på at genvinde funktionen.",
      focus: "Fokus på funktion, bevægelse og tryg, individuel behandling.",
      href: "/behandlinger/hofte",
    },
    {
      title: "Hovedpine",
      slug: "hovedpine",
      icon: "fa-light fa-head-side-brain",
      desc: "Døjer du med spændingshovedpine, migræne eller nakkerelateret hovedpine? Vi tilbyder effektiv, skånsom og individuelt tilpasset behandling.",
      focus: "Fokus på årsag, skånsom lindring og forebyggende rådgivning.",
      href: "/behandlinger/hovedpine",
    },
    {
      title: "Babyer og børn",
      slug: "babyer-og-boern",
      icon: "fa-light fa-baby",
      desc: "Blid og tryg kiropraktisk behandling af spædbørn og børn. Vi hjælper med kolik, skævt baghoved, søvnbesvær, motorik og vokseværk.",
      focus: "Fokus på tryghed, blide teknikker og en rolig oplevelse for barnet.",
      href: "/behandlinger/babyer-og-boern",
    },
    {
      title: "Gravide",
      slug: "gravide",
      icon: "fa-light fa-person-pregnant",
      desc: "Graviditeten belaster kroppen på mange måder. Vi tilbyder skånsom behandling af bækken- og lændesmerter samt gode råd til en lettere graviditet.",
      focus: "Fokus på komfort, skånsom bækkenbehandling og tryghed for mor og barn.",
      href: "/behandlinger/gravide",
    },
    {
      title: "Voksne og seniorer",
      slug: "voksne-og-seniorer",
      icon: "fa-light fa-person-cane",
      desc: "Bevar bevægeligheden og livskvaliteten hele livet. Vi behandler slidgigt, stivhed, rygsmerter og balanceproblemer med tilpassede teknikker.",
      focus: "Fokus på mobilitet, skånsom ledbehandling og bevarelse af livskvalitet.",
      href: "/behandlinger/voksne-og-seniorer",
    },
    {
      title: "Røntgen",
      slug: "roentgen",
      icon: "fa-light fa-radiation",
      desc: "Med eget moderne digitalt røntgenanlæg på klinikken sikrer vi dig en hurtig og præcis udredning af dine ryg- og ledproblemer uden ventetid.",
      focus: "Fokus på hurtig udredning, præcis diagnose og sikkerhed.",
      href: "/behandlinger/roentgen",
    },
  ];

  return (
    <div className="space-y-24 pb-24">
      {/* Header Banner */}
      <section className="bg-gradient-to-b from-brand-100/50 to-white py-16 text-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-4">
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-950">
            Behandlinger på klinikken
          </h1>
          <p className="text-lg text-brand-800 max-w-2xl mx-auto">
            Vi behandler problemer i hele bevægeapparatet. Vores mål er at lindre dine smerter, genoprette balancen og hjælpe dig godt videre.
          </p>
        </div>
      </section>

      {/* Treatments Grid */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {treatments.map((item) => (
            <div
              key={item.title}
              className="bg-white rounded-3xl border border-brand-100 p-8 sm:p-10 shadow-xs flex flex-col justify-between space-y-8"
            >
              <div className="space-y-6 text-left">
                <div className="flex items-center gap-4">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                    <i className={`${item.icon} text-3xl`} />
                  </div>
                  <h2 className="text-2xl font-bold font-display text-brand-950">
                    {item.title}
                  </h2>
                </div>
                <p className="text-brand-800 leading-relaxed text-sm">
                  {item.desc}
                </p>
                <div className="rounded-xl bg-brand-50/50 p-4 border border-brand-100/30 text-xs font-semibold text-brand-800 flex items-center gap-2.5">
                  <i className="fa-light fa-circle-info text-brand-600 text-base" />
                  {item.focus}
                </div>
              </div>

              <div className="pt-4 text-left">
                <Link
                  to={item.href}
                  className="inline-flex items-center justify-center rounded-full bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 transition-colors cursor-pointer"
                >
                  Læs mere om {item.title.toLowerCase()}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Treatment Gallery */}
      {!loading && galleryImages.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center space-y-12">
          <div className="max-w-2xl mx-auto space-y-4">
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-brand-950">
              Billeder fra behandlinger
            </h2>
            <p className="text-brand-700">
              Vi anvender blide, præcise og evidensbaserede teknikker tilpasset patienter i alle aldre.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {galleryImages.map((img, idx) => (
              <div
                key={idx}
                className="overflow-hidden rounded-2xl border border-brand-100 shadow-xs aspect-square group"
              >
                <img
                  src={`${img}?width=400&quality=80&format=auto`}
                  alt={`Behandlingsbillede ${idx + 1}`}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
